import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { OccasionThemeProvider } from "@/hooks/useOccasionTheme";
import { AuthProvider } from "@/hooks/useAuth";
import { PermissionsProvider } from "@/hooks/useUserPermissions";
import { ShippingCountriesProvider } from "@/hooks/useShippingCountries";
import { LogoSettingsProvider } from "@/hooks/useLogoSettings";
import { LanguageProvider } from "@/hooks/useLanguage";
import { usePreloadLoadingScreen } from "@/hooks/usePreloadLoadingScreen";
import ScrollToTop from "@/components/ScrollToTop";
import ValentineDecorations from "@/components/ValentineDecorations";
import RamadanDecorations from "@/components/RamadanDecorations";
import { TrackingPixels } from "@/components/TrackingPixels";
import { lazy, Suspense } from "react";

// Eagerly load the homepage for best initial load
import Index from "./pages/Index";

// Lazy load all other pages to reduce initial bundle size
const Quiz = lazy(() => import("./pages/Quiz"));
const Results = lazy(() => import("./pages/Results"));
const Selection = lazy(() => import("./pages/Selection"));
const Personalization = lazy(() => import("./pages/Personalization"));
const LoginDetails = lazy(() => import("./pages/LoginDetails"));
const ShippingAddressPage = lazy(() => import("./pages/ShippingAddress"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Cart = lazy(() => import("./pages/Cart"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const WhoWeAre = lazy(() => import("./pages/WhoWeAre"));
const OriginOfAnaya = lazy(() => import("./pages/OriginOfAnaya"));
const EssenceOfAnaya = lazy(() => import("./pages/EssenceOfAnaya"));
const MissionVision = lazy(() => import("./pages/MissionVision"));
const Sustainability = lazy(() => import("./pages/Sustainability"));
const SpecialOccasions = lazy(() => import("./pages/SpecialOccasions"));
const AnayaCollection = lazy(() => import("./pages/AnayaCollection"));
const CollectionProductDetail = lazy(() => import("./pages/CollectionProductDetail"));
const CorporateGifting = lazy(() => import("./pages/CorporateGifting"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Contact = lazy(() => import("./pages/Contact"));
const ReturnsRefunds = lazy(() => import("./pages/ReturnsRefunds"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const Account = lazy(() => import("./pages/Account"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const TechnicianDashboard = lazy(() => import("./pages/TechnicianDashboard"));
const QADashboard = lazy(() => import("./pages/QADashboard"));
const DriverDashboard = lazy(() => import("./pages/DriverDashboard"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/PaymentCanceled"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));
const PerfumeWizard = lazy(() => import("./pages/PerfumeWizard"));
const WizardOrder = lazy(() => import("./pages/WizardOrder"));
const CorporateGiftingWizard = lazy(() => import("./pages/CorporateGiftingWizard"));
const AssistedOrder = lazy(() => import("./pages/AssistedOrder"));
const SalesmanOrders = lazy(() => import("./pages/SalesmanOrders"));
const PayPurchase = lazy(() => import("./pages/PayPurchase"));

const queryClient = new QueryClient();

// Minimal loading fallback - just an empty div to avoid layout shift
const PageLoading = <div className="min-h-screen bg-background" />;

// Component that handles preloading
const AppContent = () => {
  // Preload loading screen video on app initialization
  usePreloadLoadingScreen();

  return (
    <>
      <ScrollToTop />
      <TrackingPixels />
      <ValentineDecorations />
      <RamadanDecorations />
      <Suspense fallback={PageLoading}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/results" element={<Results />} />
          <Route path="/selection" element={<Selection />} />
          <Route path="/personalization" element={<Personalization />} />
          <Route path="/login-details" element={<LoginDetails />} />
          <Route path="/shipping-address" element={<ShippingAddressPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/wizard-order" element={<WizardOrder />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/who-we-are" element={<WhoWeAre />} />
          <Route path="/origin-of-anaya" element={<OriginOfAnaya />} />
          <Route path="/essence-of-anaya" element={<EssenceOfAnaya />} />
          <Route path="/mission-vision" element={<MissionVision />} />
          <Route path="/sustainability" element={<Sustainability />} />
          <Route path="/special-occasions" element={<SpecialOccasions />} />
          <Route path="/anaya-collection" element={<AnayaCollection />} />
          <Route path="/collection/:slug" element={<CollectionProductDetail />} />
          <Route path="/corporate-gifting" element={<CorporateGifting />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogArticle />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/returns-refunds" element={<ReturnsRefunds />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          <Route path="/terms-conditions" element={<Navigate to="/terms-and-conditions" replace />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/account" element={<Account />} />
          <Route path="/order/:id" element={<OrderDetails />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/technician" element={<TechnicianDashboard />} />
          <Route path="/qa" element={<QADashboard />} />
          <Route path="/driver" element={<DriverDashboard />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-canceled" element={<PaymentCanceled />} />
          <Route path="/page/:pageKey" element={<DynamicPage />} />
          <Route path="/perfume-wizard" element={<PerfumeWizard audience="public" />} />
          <Route path="/corporate-gifting-wizard" element={<CorporateGiftingWizard audience="public" />} />
          <Route path="/salesman/perfume-wizard" element={<PerfumeWizard />} />
          <Route path="/salesman/corporate-gifting-wizard" element={<CorporateGiftingWizard />} />
          <Route path="/salesman/order" element={<AssistedOrder />} />
          <Route path="/salesman/orders" element={<SalesmanOrders />} />
          <Route path="/pay/:token" element={<PayPurchase />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PermissionsProvider>
          <LanguageProvider>
            <CurrencyProvider>
              <ShippingCountriesProvider>
                <LogoSettingsProvider>
                  <OccasionThemeProvider>
                    <TooltipProvider>
                      <Toaster />
                      <Sonner />
                      <BrowserRouter>
                        <AppContent />
                      </BrowserRouter>
                    </TooltipProvider>
                  </OccasionThemeProvider>
                </LogoSettingsProvider>
              </ShippingCountriesProvider>
            </CurrencyProvider>
          </LanguageProvider>
        </PermissionsProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
